/**
 * RoomProductTileUI - Pure UI component for displaying a single product (SKU) in the room.
 * @param {Object} props
 * @param {Object} props.sku - SKU data object
 * @param {Function} props.handleNotifyMe - Handler for notify me action
 * @param {Function} props.handleAddToCart - Handler for add to cart action
 * @param {Function} props.handleChange - Handler for quantity change
 * @param {boolean} props.inventoryHydrated - Whether inventory has been hydrated on the client
 * @returns {JSX.Element}
 */
import React, { useEffect, useState } from "react"
import Button from "@/components/core/Button/Button"
import QuantitySelector from "@/components/cart/QuantitySelector"
import DisplayDiscountPrice from "@/components/Shared/DisplayDiscountPrice"
import { getFavIconStatus } from "@/components/ProductList/v2/ProductTile/utils"
import wishIcon from "@/public/icons/wish.svg"
import MissingImage from "@/public/images/image-missing.png"
import ImagePlaceHolder from "@/components/core/Image/ImagePlaceHolder"
import { tooltip } from "@/constants/index"
import {
  selectAuthState,
  setAuthModalVisibility,
} from "@/store/features/authSlice"
import { useDispatch, useSelector } from "react-redux"
import {
  getFavouraties,
  getPDPUrlForProductTile,
  getShortenedUrl,
  isCanadaSite,
} from "@/utils/helper"
import { selectGenericState, showToast } from "@/store/features/genericSlice"
import ListConfirmationModal from "@/components/ListConfirmationModal"
import SaveToList from "@/components/SaveToList"
import {
  getMyAccountListsWithLineItems,
  selectFavoritesState,
  addProduct,
  removeItems,
} from "@/store/features/favoritesSlice"
import filledWishIcon from "@/public/icons/wishlist-gray-filled.svg"
import { useTranslation } from "next-i18next"
import _isEmpty from "lodash/isEmpty"
import { getPresetUrl } from "@/utils/index"
import useWindowResize from "@/hooks/useWindowResize"
import {
  datlayerForProductAddToWishlist,
  getProductInfo,
} from "@/components/ShopTheRoomDetails/Analytics/index"
import { NumericFormat } from "react-number-format"
import cx from "classnames"
import RoomListPriceTooltip from "@/components/ShopTheRoomDetails/ui/TooltipDSM"

const RoomProductTileUI = ({
  sku = {},
  handleNotifyMe = () => {},
  handleAddToCart = () => {},
  handleChange = () => {},
  inventoryHydrated = false,
  i18n = {},
  presetConfig = {},
  swatchUrl = "",
  skuPrepend = "",
}) => {
  const [imgLoaded, setImgLoaded] = useState(false)
  const [imgError, setImgError] = useState(false)
  const { toaster, featureFlags = {} } = useSelector(selectGenericState)
  const { isAuth = false } = useSelector(selectAuthState)
  const { ENABLE_SHOPPING_LISTS_V2: isSaveProductToList = false } = featureFlags
  const [openModal, setOpenModal] = useState(false)
  const [deleteFromList, setDeleteFromList] = useState(false)
  const [productSavedToList, setProductSavedToList] = useState(false)
  const { userListItemsSkus = {} } = useSelector(selectFavoritesState)
  const [showAddToList, setShowAddToList] = useState(false)
  const [isFavorite, setIsFavorite] = useState(false)
  const [loginFavouriteCallback, setLoginFavouriteCallback] = useState(null)
  const [toasterContent, setToasterContent] = useState({
    successMessage: "",
    unfavoriteSuccessMessage: "",
  })
  const [productDetailLink, setProductDetailLink] = useState("")

  const {
    globalConfig: {
      internationalization: { currencySign = "$" } = {},
      general: { siteName = "" } = {},
    } = {},
  } = useSelector(selectGenericState)
  const {
    ProductBrandName_s: name = "",
    ProductDescriptionProductShort_s: description = "",
    imageUrl = "",
    productDiscontinued_b: productDiscontinued = "false",
    sku_s: skuCode,
    slug_s: slug,
    ProductLocalCategory_s: ProductLocalCategory,
    availableQuantity = "",
    currentInventory = 0,
    itemQuantity = 1,
    showMsg,
    loading,
    price,
    listPrice,
    discountedPrice,
  } = sku || {}

  const { t } = useTranslation()
  const texts = {
    removeItem: t("kf.favorites.removeItem"),
    removeItemError: t("kf.favorites.removeItemError"),
    favSuccessMsg: t("kf.favorites.success"),
    favErrorMsg: t("kf.favorites.error"),
  }

  const datamain = sku?.["id"]?.split("|")?.[0] || ""
  const favorites = getFavouraties()?.lineItems || []
  const dispatch = useDispatch()
  const [width] = useWindowResize()

  const handleOnChange = qty => {
    handleChange(skuCode, qty)
  }

  const parts = imageUrl ? imageUrl.split("/") : []
  const imageAssetID = parts.length > 0 ? parts[parts.length - 1] : ""

  useEffect(() => {
    let isActive = true
    const fetchPdpUrl = async () => {
      try {
        const pdpUrlPath = await getPDPUrlForProductTile({
          ProductLocalCategory_s: ProductLocalCategory,
          slug_s: slug,
        })

        if (!isActive) return

        setProductDetailLink(pdpUrlPath ? `${pdpUrlPath}?skuId=${skuCode}` : "")
      } catch {
        if (!isActive) return
        setProductDetailLink("")
      }
    }

    fetchPdpUrl()
    return () => {
      isActive = false
    }
  }, [ProductLocalCategory, slug, skuCode])

  const handleNotifyLinkClick = event => {
    if (!productDetailLink) {
      event.preventDefault()
      return
    }
    handleNotifyMe(productDetailLink, skuCode, sku)
  }

  const handleNotifyButtonClick = e => {
    handleNotifyMe(
      productDetailLink,
      skuCode,
      sku,
      productDiscontinued ? i18n.availColorCTA : i18n.notifyMailCTA
    )
    location.href = productDetailLink
    e.preventDefault()
    e.stopPropagation()
  }

  const handleSaveToListSuccessCallBack = () => {
    setProductSavedToList(true)
  }

  const handleLogin = e => {
    datlayerForProductAddToWishlist(sku, "save to list", skuPrepend)
    e.preventDefault()
    e.stopPropagation()
    window.loginFavouriteCallback = () => {
      setLoginFavouriteCallback(e)
    }
    dispatch(setAuthModalVisibility({ show: true }))
  }

  const setToasterMessage = async () => {
    if (!_isEmpty(toaster?.content)) {
      const { successMessage, unfavoriteSuccessMessage } = toaster.content || {}
      setToasterContent({
        successMessage: successMessage
          ? await getShortenedUrl(successMessage)
          : "",
        unfavoriteSuccessMessage: unfavoriteSuccessMessage
          ? await getShortenedUrl(unfavoriteSuccessMessage)
          : "",
      })
    }
  }

  useEffect(() => {
    setToasterMessage()
  }, [])

  useEffect(() => {
    if (loginFavouriteCallback && isAuth) {
      if (isSaveProductToList) {
        dispatch(getMyAccountListsWithLineItems())
          .unwrap()
          .then(res => {
            const userlistSKUS = res[0][1]
            if (userlistSKUS[skuCode]) {
              setOpenModal(true)
              setDeleteFromList(true)
              datlayerForProductAddToWishlist(
                sku,
                "delete from list",
                skuPrepend,
                true
              )
              return
            } else {
              handleFavorite(loginFavouriteCallback)
            }
          })
      } else {
        handleFavorite(loginFavouriteCallback)
      }
    }
  }, [loginFavouriteCallback, isAuth])

  useEffect(() => {
    setIsFavorite(
      favorites?.find(vl => vl?.productId === datamain) ? true : false
    )
  }, [favorites, isAuth, sku, skuCode, isFavorite])

  const handleFavorite = e => {
    e.preventDefault()
    e.stopPropagation()
    if (isSaveProductToList) {
      if (userListItemsSkus?.[skuCode]) {
        setOpenModal(true)
        setDeleteFromList(true)
        datlayerForProductAddToWishlist(
          sku,
          "delete from list",
          skuPrepend,
          true
        )
        return
      }
      setShowAddToList(true)
      if (typeof window !== "undefined") {
        window.loginFavouriteCallback = null
      }
      setLoginFavouriteCallback(null)
      datlayerForProductAddToWishlist(sku, "save to list", skuPrepend)
      return
    }
    if (isFavorite === true) {
      favorites?.map(fav => {
        if (fav?.productId === datamain) {
          if (!isAuth) {
            handleLogin(e)
          } else {
            dispatch(removeItems({ ids: [fav.id], isDataLayerNeeded: false }))
              .unwrap()
              .then(() => {
                dispatch(
                  showToast({
                    message:
                      toasterContent.unfavoriteSuccessMessage ||
                      texts.removeItem,
                    isVisible: true,
                  })
                )
              })
              .catch(err => {
                dispatch(
                  showToast({
                    message: texts?.removeItemError || "",
                    isVisible: true,
                  })
                )
              })
          }
        }
      })
    } else {
      if (!isAuth) {
        handleLogin(e)
      } else {
        dispatch(addProduct(skuCode))
          .unwrap()
          .then(() => {
            dispatch(
              showToast({
                message: toasterContent.successMessage || texts.favSuccessMsg,
                isVisible: true,
              })
            )
          })
          .catch(err => {
            dispatch(
              showToast({
                message: texts?.favErrorMsg || "",
                isVisible: true,
              })
            )
          })
      }
    }
    window.loginFavouriteCallback = null
    setLoginFavouriteCallback(null)
  }

  const ProductInfo = getProductInfo(sku, skuPrepend)

  const imageContainerClass = `shop-the-room-inspiration__product-image${
    !imgLoaded ? " shop-the-room-inspiration__product-image--loading" : ""
  }`

  // Logic for Add to Cart vs Notify Me
  const showAddToCart = !inventoryHydrated || availableQuantity > 0

  return (
    <div className="shop-the-room-inspiration__product-card">
      <div className={imageContainerClass}>
        <div className="shop-the-room-inspiration__favorite">
          <button
            id={`kf-add-to-fav-`}
            className="shop-the-room-inspiration__favorite-icon"
            tabIndex="0"
            onClick={isAuth ? e => handleFavorite(e) : handleLogin}
          >
            <img
              role="presentation"
              src={getFavIconStatus({
                isSaveProductToList,
                productSavedToList,
                inSavedItems: userListItemsSkus?.[skuCode],
                isFavorite,
                filledWishIcon,
                wishIcon,
              })}
              alt={"texts.wishlist"}
            />
          </button>
        </div>
        {(!productDetailLink || (!imgLoaded && imageUrl && !imgError)) && (
          <ImagePlaceHolder assetID="product-tile" />
        )}

        <a
          tabIndex="-1"
          href={productDetailLink}
          onClick={handleNotifyLinkClick}
        >
          {/* Show missing image only once link is ready and image is absent/broken */}
          {productDetailLink && (!imageUrl || imgError) && (
            <img
              src={MissingImage.src}
              alt={
                name
                  ? `${name} - image unavailable`
                  : "Product image unavailable"
              }
            />
          )}
          {/* Render actual image only once productDetailLink is available */}
          {productDetailLink && imageUrl && !imgError && (
            <img
              alt="Product"
              src={getPresetUrl(
                width,
                0,
                swatchUrl,
                imageAssetID,
                presetConfig
              )}
              style={{ display: imgLoaded && !imgError ? "block" : "none" }}
              onLoad={() => setImgLoaded(true)}
              onError={() => {
                setImgLoaded(true)
                setImgError(true)
              }}
            />
          )}
        </a>
      </div>
      <div
        className={cx({
          "shop-the-room-inspiration__product-details": true,
          "shop-the-room-inspiration__product-details--width-ca":
            width > 1024 && isCanadaSite(siteName),
        })}
      >
        {productDiscontinued ? (
          <div className="shop-the-room-inspiration__discontinuted">
            {i18n?.discontinued}
          </div>
        ) : null}
        <div className="shop-the-room-inspiration__produc-name">
          {productDetailLink ? (
            <a
              tabIndex="-1"
              href={productDetailLink}
              onClick={handleNotifyLinkClick}
            >
              {name}
            </a>
          ) : (
            <p className="shimmer-loading__para shimmer width-170" />
          )}
        </div>
        <div className="shop-the-room-inspiration__produc-decs">
          {description}
        </div>
        {!listPrice && !price ? (
          <div className="product-detail-page__price-value">
            <span>
              <p className="shimmer-loading__para shimmer width-170" />
            </span>
          </div>
        ) : (
          <>
            <div className="shop-the-room-inspiration__produc-price">
              {discountedPrice > 0 && !isCanadaSite(siteName) ? (
                <>
                  <DisplayDiscountPrice
                    discountedPrice={discountedPrice}
                    price={price}
                    color={true}
                    currencySign={currencySign}
                  />
                  <div className="shop-the-room-inspiration__list-price-actual">
                    <NumericFormat
                      value={listPrice}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={currencySign}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                    <div className="shop-the-room-inspiration__list-price-tool-tip">
                      <RoomListPriceTooltip></RoomListPriceTooltip>
                    </div>
                  </div>
                </>
              ) : (
                <div className="shop-the-room-inspiration__list-price">
                  {!isCanadaSite(siteName) ? (
                    <>
                      <div className="shop-the-room-inspiration__list-price-discount">
                        <NumericFormat
                          value={price}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={currencySign}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                      </div>
                      <div className="shop-the-room-inspiration__list-price-actual">
                        <NumericFormat
                          value={listPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={currencySign}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                        <div className="shop-the-room-inspiration__list-price-tool-tip">
                          <RoomListPriceTooltip></RoomListPriceTooltip>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="shop-the-room-inspiration__list-price-discount">
                      <NumericFormat
                        value={listPrice}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={currencySign}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* if not canada site */}
            {!isCanadaSite(siteName) && (
              <>
                {!showMsg &&
                ((itemQuantity === 2 && availableQuantity === 2) ||
                  (currentInventory === 2 && availableQuantity === 2)) ? (
                  <div className="shop-the-room-inspiration__Items-to-ship">
                    <img src={tooltip} alt="tool-tip" />
                    <div className="shop-the-room-inspiration__msg-for-item-to-ship">
                      {i18n?.twoItemAvailable}
                    </div>
                  </div>
                ) : null}

                {showMsg && availableQuantity !== 0 ? (
                  <div className="shop-the-room-inspiration__Items-to-ship">
                    <img src={tooltip} alt="tool-tip" />
                    <div className="shop-the-room-inspiration__msg-for-item-to-ship">
                      {i18n?.noItemAvailable}
                    </div>
                  </div>
                ) : null}
                {showAddToCart ? (
                  <div className="shop-the-room-inspiration__qantity-slector-add-to-cart">
                    <div className="shop-the-room-inspiration__quantity">
                      <QuantitySelector
                        value={itemQuantity}
                        onChange={handleOnChange}
                        size="small"
                        loading={loading}
                      />
                    </div>
                    <div className="shop-the-room-inspiration__add-to-cart">
                      <Button
                        role="button"
                        label={i18n?.addToCart}
                        size="large"
                        type="primary"
                        flexible={true}
                        loading={loading}
                        onClick={() => handleAddToCart(skuCode, sku)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="shop-the-room-inspiration__notify-me">
                    {!productDiscontinued && availableQuantity === 0 ? (
                      <div className="shop-the-room-inspiration__out-of-stock">
                        {i18n?.outOfStock}
                      </div>
                    ) : null}
                    <div className="shop-the-room-inspiration__notification">
                      <Button
                        role="button"
                        label={
                          productDiscontinued
                            ? i18n?.availColorCTA
                            : i18n?.notifyMailCTA
                        }
                        size="large"
                        type="primary"
                        flexible={true}
                        loading={!productDetailLink}
                        onClick={handleNotifyButtonClick}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {openModal ? (
        <ListConfirmationModal
          openConforMationModal={openModal}
          deleteFromList={deleteFromList}
          closeConformationModal={() => setOpenModal(false)}
          list={userListItemsSkus?.[skuCode]}
          deleteConfirmationModal={() => setProductSavedToList(false)}
        />
      ) : showAddToList ? (
        <SaveToList
          productInfoPdp={ProductInfo}
          productSku={skuCode}
          showListSaveAndCreate={showAddToList}
          listSaveAndCreateClose={() => {
            setShowAddToList(false)
          }}
          handleSuccessCallBack={handleSaveToListSuccessCallBack}
        />
      ) : null}
    </div>
  )
}
export default RoomProductTileUI
